/**
 * Backup codes management component.
 * Allows users to view and regenerate their backup/recovery codes.
 */

import { useState, useEffect, useCallback } from 'react';
import type { SettingsFlow, UiNodeInputAttributes } from '@ory/client';
import { kratos } from '../lib/kratos';
import LoadingSpinner from './LoadingSpinner';

interface BackupCodesProps {
  /** Current settings flow ID */
  flowId: string;
  /** Callback when codes are successfully regenerated */
  onRegenerate?: () => void;
}

type LookupAction = 'lookup_secret_regenerate' | 'lookup_secret_confirm' | 'lookup_secret_reveal';

const lookupNode = (flow: SettingsFlow, name: string) =>
  flow.ui.nodes.find(
    (n) => n.group === 'lookup_secret' && (n.attributes as UiNodeInputAttributes).name === name,
  );

/** Codes Kratos shows after regenerate / reveal (text node, or secrets in its context). */
function codesFromFlow(flow: SettingsFlow): string[] {
  for (const node of flow.ui.nodes.filter((n) => n.group === 'lookup_secret')) {
    const attrs = node.attributes as unknown as {
      secrets?: { text: string }[] | string[];
      text?: { text?: string; context?: { secrets?: { text: string }[] } };
    };
    const ctxSecrets = attrs.text?.context?.secrets;
    if (ctxSecrets?.length) return ctxSecrets.map((x) => x.text);
    if (Array.isArray(attrs.secrets) && attrs.secrets.length) {
      return attrs.secrets.map((x) => (typeof x === 'string' ? x : x.text));
    }
    if (node.type === 'text' && attrs.text?.text) {
      return attrs.text.text.split(/[\n,\s]+/).map((c) => c.trim()).filter((c) => c.length > 0);
    }
  }
  return [];
}

export default function BackupCodes({ flowId, onRegenerate }: BackupCodesProps) {
  const [codes, setCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCodes, setShowCodes] = useState(false);
  const [hasLookupSecret, setHasLookupSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  // Kratos only stores new codes after lookup_secret_confirm
  const [pendingConfirm, setPendingConfirm] = useState(false);

  const fetchBackupCodes = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: flow } = await kratos.getSettingsFlow({ id: flowId });

      // Get CSRF token
      const csrfNode = flow.ui.nodes.find((node) => {
        const attrs = node.attributes as UiNodeInputAttributes;
        return attrs.name === 'csrf_token';
      });

      if (csrfNode) {
        const attrs = csrfNode.attributes as UiNodeInputAttributes;
        setCsrfToken(attrs.value as string);
      }

      // Find lookup_secret nodes
      const lookupNodes = flow.ui.nodes.filter(
        (node) => node.group === 'lookup_secret'
      );

      // Check if lookup_secret is already configured
      const revealNode = lookupNodes.find((node) => {
        const attrs = node.attributes as UiNodeInputAttributes;
        return attrs.name === 'lookup_secret_reveal';
      });

      // Kratos offers reveal/disable only once codes exist; regenerate alone means none yet
      const disableNode = lookupNodes.find((node) => {
        const attrs = node.attributes as UiNodeInputAttributes;
        return attrs.name === 'lookup_secret_disable';
      });
      setHasLookupSecret(!!(revealNode || disableNode));
      setPendingConfirm(!!lookupNode(flow, 'lookup_secret_confirm'));
      const visible = codesFromFlow(flow);
      setCodes(visible);
      setShowCodes(visible.length > 0);
    } catch (err) {
      console.error('Fetch backup codes error:', err);
      setError('Failed to load backup codes.');
    } finally {
      setIsLoading(false);
    }
  }, [flowId]);

  useEffect(() => {
    fetchBackupCodes();
  }, [fetchBackupCodes]);

  const submitLookup = async (action: LookupAction) => {
    setIsRegenerating(true);
    setError(null);

    try {
      const { data: flow } = await kratos.updateSettingsFlow({
        flow: flowId,
        updateSettingsFlowBody: {
          method: 'lookup_secret',
          [action]: true,
          csrf_token: csrfToken || undefined,
        } as Parameters<typeof kratos.updateSettingsFlow>[0]['updateSettingsFlowBody'],
      });

      if (action === 'lookup_secret_confirm') {
        setPendingConfirm(false);
        setHasLookupSecret(true);
        onRegenerate?.();
        return;
      }
      const shown = codesFromFlow(flow);
      setCodes(shown);
      setShowCodes(shown.length > 0);
      setPendingConfirm(!!lookupNode(flow, 'lookup_secret_confirm'));
    } catch (err) {
      console.error('Backup codes error:', err);
      setError(
        action === 'lookup_secret_confirm' ? 'Failed to save backup codes.'
          : action === 'lookup_secret_reveal' ? 'Failed to show backup codes.'
            : 'Failed to generate backup codes.',
      );
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleRegenerate = () => submitLookup('lookup_secret_regenerate');

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleDownload = () => {
    const content = [
      'Passport Backup Codes',
      '=============================',
      '',
      'Keep these codes safe! Each code can only be used once.',
      'Use them to log in if you lose access to your authenticator app.',
      '',
      ...codes,
      '',
      `Generated: ${new Date().toISOString()}`,
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = 'orbital-passport-backup-codes.txt';
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="py-6">
        <LoadingSpinner text="Loading backup codes..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-content-primary">Backup Codes</h4>
          <p className="text-xs text-content-tertiary">
            Use these codes if you lose access to your authenticator app
          </p>
        </div>
        {hasLookupSecret && (
          <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
            Enabled
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Show codes if available */}
      {showCodes && codes.length > 0 && (
        <div className="bg-surface-secondary rounded-lg p-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex">
              <svg className="h-5 w-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm text-yellow-700 font-medium">Save these codes!</p>
                <p className="text-xs text-yellow-600">
                  Store them securely. Each code can only be used once.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            {codes.map((code, index) => (
              <code
                key={index}
                className="bg-surface-primary px-3 py-2 rounded border text-sm font-mono text-center"
              >
                {code}
              </code>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleCopyAll}
              className="flex-1 text-sm px-3 py-2 text-orbital-600 hover:bg-orbital-50 rounded-md transition-colors"
            >
              {copied ? 'Copied!' : 'Copy All'}
            </button>
            <button
              onClick={handleDownload}
              className="flex-1 text-sm px-3 py-2 text-orbital-600 hover:bg-orbital-50 rounded-md transition-colors"
            >
              Download
            </button>
          </div>
        </div>
      )}

      {pendingConfirm && codes.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-orange-600 text-center">
            These codes are not active until you confirm you've saved them.
          </p>
          <button
            onClick={() => submitLookup('lookup_secret_confirm')}
            disabled={isRegenerating}
            className="btn-primary w-full"
          >
            {isRegenerating ? <LoadingSpinner size="sm" /> : "I've saved these codes"}
          </button>
        </div>
      )}

      {hasLookupSecret && !pendingConfirm && !showCodes && (
        <button
          onClick={() => submitLookup('lookup_secret_reveal')}
          disabled={isRegenerating}
          className="w-full text-sm px-4 py-2 rounded-lg text-orbital-600 hover:bg-orbital-50 transition-colors"
        >
          Show my backup codes
        </button>
      )}

      {/* Generate/Regenerate button */}
      {!pendingConfirm && <button
        onClick={handleRegenerate}
        disabled={isRegenerating}
        className={`w-full text-sm px-4 py-2 rounded-lg transition-colors ${
          hasLookupSecret
            ? 'border border-orange-300 text-orange-600 hover:bg-orange-50'
            : 'btn-primary'
        }`}
      >
        {isRegenerating ? (
          <LoadingSpinner size="sm" />
        ) : hasLookupSecret ? (
          'Regenerate Codes (invalidates old codes)'
        ) : (
          'Generate Backup Codes'
        )}
      </button>}

      {!hasLookupSecret && (
        <p className="text-xs text-content-tertiary text-center">
          Generate backup codes to use if you lose your phone or authenticator app.
        </p>
      )}
    </div>
  );
}
